import { Paper } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { DelayForPareto } from "../models/DelayForPareto";

type Props = {
	delaysForPareto: DelayForPareto[];
};

export const DelaysParetoGraphic = ({ delaysForPareto }: Props) => {
	const formatMinutes = (minutes: number) => {
		return new Intl.NumberFormat("en-US").format(minutes);
	};

	const getChartOptions = () => {
		const paretoData = delaysForPareto.map((item) => {
			const delayPath = item.DelayCodePath;
			let formattedDelayName = "";

			if (delayPath) {
				const parts = delayPath.split("/");
				// Filter out any empty strings that result from leading/trailing slashes
				const relevantParts = parts.filter((part) => part !== "");

				if (relevantParts.length >= 2) {
					// Get the second to last and the last part
					formattedDelayName = `${relevantParts[relevantParts.length - 2]}/${
						relevantParts[relevantParts.length - 1]
					}`;
				} else if (relevantParts.length === 1) {
					// If there's only one part (e.g., "/Delay1"), just use that
					formattedDelayName = relevantParts[0];
				}
			}

			return {
				...item,
				DelayCodeName: formattedDelayName || "",
				DelayCategoryName: item.DelayCategoryName || "",
				DelayCategoryColor: item.DelayCategoryColor || "#1890ff",
			};
		});

		const options = {
			tooltip: {
				trigger: "axis",
				axisPointer: {
					type: "shadow",
				},
				formatter: (params: any) => {
					const dataPoint = params[0];
					const currentItem = paretoData[dataPoint.dataIndex];
					const categoryInfo = currentItem.DelayCategoryName
						? `<br/>Category: ${currentItem.DelayCategoryName}`
						: "";

					return `${currentItem.DelayCodeName}<br/>
                  Total Minutes: ${formatMinutes(
										dataPoint.value,
									)} min${categoryInfo}`;
				},
			},
			grid: {
				left: "5%",
				right: "4%",
				bottom: "3%",
				containLabel: true,
			},
			xAxis: [
				{
					type: "category",
					data: paretoData.map((item) => item.DelayCodeName),
					axisTick: {
						alignWithLabel: true,
					},
					axisLabel: {
						rotate: 45,
						interval: 0,
					},
				},
			],
			yAxis: [
				{
					type: "value",
					name: "Minutes",
					nameLocation: "middle",
					nameGap: 50,
					splitLine: {
						show: true,
					},
					axisLine: {
						show: true,
					},
					axisTick: {
						show: true,
					},
					axisLabel: {
						show: true,
						formatter: (value: number) => {
							return formatMinutes(value);
						},
					},
				},
			],
			series: [
				{
					name: "Delay Minutes",
					type: "bar",
					barWidth: "60%",
					data: paretoData.map((item) => item.TotalDelayMinutes || 0),
					itemStyle: {
						color: (params: any) => {
							const item = paretoData[params.dataIndex];
							if (item.DelayCodeName === "Undeclared Delays") {
								return "#CE0000";
							}
							if (item.DelayCodeName === "MicroDelays") {
								return "#D100DA";
							}
							return item.DelayCategoryColor;
						},
					},
					label: {
						show: true,
						position: "top",
						formatter: (params: any) => {
							return formatMinutes(params.value);
						},
					},
				},
			],
		};

		return options;
	};

	return (
		<div onContextMenu={(e) => e.preventDefault()} style={{ width: "100%" }}>
			<Paper
				sx={{
					height: "65vh",
					minHeight: "65vh",
					width: "100%",
				}}
				elevation={2}
			>
				<ReactECharts
					option={getChartOptions()}
					style={{ height: "100%", width: "100%" }}
					opts={{ renderer: "svg" }}
				/>
			</Paper>
		</div>
	);
};
